import { SphereBarChart } from "@components/common/sphere-bar-chart/sphere-bar-chart";
import { Box, Skeleton } from "@mui/material";
import { ProjectChartController } from "@pages/project-details/project-overview/project-chart/project-chart-controller";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import {
  fetchingAnalyticsFlagsSelector,
  projectChartSelector,
} from "@store/analytics/analytics-selector";
import { fetchProjectStats } from "@store/analytics/analytics-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect, useMemo } from "react";

/* eslint-disable @typescript-eslint/no-magic-numbers -- aspect ratio value */
/** Chart aspect ratio */
const CHART_ASPECT_RATIO = 21 / 9;
/* eslint-enable @typescript-eslint/no-magic-numbers */

interface Props {
  /** Whether the project is still loading and skeleton should be shown instead */
  isLoading: boolean;
}

/** Renders an analytics chart that displays the current project stats */
export function ProjectChart({ isLoading }: Props): JSX.Element {
  const { projectId } = useAppParams();
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const { isFetchingProjectStats } = useAppSelector(
    fetchingAnalyticsFlagsSelector
  );
  const chartData = useAppSelector(projectChartSelector);

  const shouldShowSkeleton = useMemo(() => {
    return isLoading || isFetchingProjectStats || !chartData;
  }, [chartData, isFetchingProjectStats, isLoading]);

  // Fetches the project stats
  useEffect(() => {
    if (projectId) {
      void dispatch(fetchProjectStats({ projectId, companyId }));
    }
  }, [companyId, dispatch, projectId]);

  return (
    <Box>
      {shouldShowSkeleton ? (
        <Skeleton variant="rectangular" width="100%">
          <ProjectChartController />
        </Skeleton>
      ) : (
        <ProjectChartController />
      )}
      <SphereBarChart
        data={chartData}
        aspectRatio={CHART_ASPECT_RATIO}
        shouldShowSkeleton={shouldShowSkeleton}
        shouldHideTitle
      />
    </Box>
  );
}
